import { InfoIconSmall } from '@src/svgs/InfoIconSmall';
import React, {
  forwardRef,
  useEffect,
  useId,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { Tooltip } from 'react-tooltip';
import styled, { css } from 'styled-components';

import { PARAGRAPH_TOOLTIP_STYLE } from './tooltipStyles';
import { TOP_LAYER_Z } from './topLayerZ';
import { useAnchoredDropdown } from './useAnchoredDropdown';

type Props = {
  values: {
    /**
     * Caption shown in the dropdown row (and in the trigger button when
     * this option is the selected value). Typed as ReactNode so callers
     * can compose an icon + text without widening the dropdown API.
     */
    caption: React.ReactNode;
    value: string | null;
    /**
     * Action rows fire `action` on click but are NOT committed as the
     * selected value — the trigger keeps showing the previously selected
     * option. Use for command entries (e.g. "+ Add language", "Remove")
     * that open a modal or run a side effect rather than changing the
     * dropdown's value. Defaults to false (normal selectable option).
     */
    isAction?: boolean;
    /**
     * Heading rows label the group of options that follows (the dropdown's
     * `<optgroup>`): rendered muted and non-interactive, never selectable.
     * Give each a unique `value` (it is only used as the React key).
     */
    isHeading?: boolean;
    /**
     * Explainer shown in a tooltip on an info icon at the end of this row.
     * Row-only: the trigger shows just the caption.
     */
    info?: string;
  }[];
  styleFont?: boolean;
  width?: string;
  action: (propertyName: string, value: string | null) => void;
  propertyName: string;
  defaultValue: string | undefined | null;
  getValue?: (value: string) => void;
  className?: string;
  /**
   * Class applied to the portaled menu panel (`Dropdown`). The menu renders
   * in a body-level portal, so wrapper-scoped CSS (e.g. a styled parent's
   * descendant rules) can't reach it — pass a class here to style the menu
   * from the calling component. See `TranslationToggle` for the canonical
   * use (revealing the full-language-name row + widening the panel).
   */
  menuClassName?: string;
  /**
   * Optional node rendered at the TOP of the portaled menu, above the value
   * rows. Unlike a value row it is not a selectable item and does not close
   * the menu on click — use it for an in-menu control (e.g. the Original↔Dubbed
   * audio toggle in `TranslationToggle`). Omitted ⇒ no header.
   *
   * May also be a function receiving a `close` callback, for header controls
   * that SHOULD dismiss the menu on click (e.g. the "Dub with AI Voice" pill,
   * which opens a modal — PLAT-10664).
   */
  menuHeader?: React.ReactNode | ((close: () => void) => React.ReactNode);
  /**
   * Optional node rendered at the BOTTOM of the portaled menu, below the value
   * rows. Like `menuHeader` it is not a selectable item; use it for an in-menu
   * control that should follow the list (e.g. the AI Voice / dub zone in
   * `TranslationToggle`). Supports the same node-or-`(close)=>node` shape.
   * Omitted ⇒ no footer.
   */
  menuFooter?: React.ReactNode | ((close: () => void) => React.ReactNode);
  /**
   * Fired whenever the menu opens or closes. Lets a caller react to the open
   * state — e.g. hide the trigger's hover tooltip while the menu is open.
   */
  onOpenChange?: (open: boolean) => void;
  /**
   * Don't render the row for the currently selected value. The entry stays in
   * `values` (the trigger still shows its caption); only its row is skipped. For
   * a menu whose pinned header already represents the current selection, so the
   * list holds only the OTHER options (the transcript language menu's "Switch
   * To" list — PLAT-11563). Defaults to false.
   */
  hideSelectedRow?: boolean;
  /**
   * Cap (px) on the menu's height before it scrolls internally. Defaults to
   * 260. Raise it for a menu with pinned chrome + a long list so more rows show
   * at once (e.g. the language picker's AI-Voice footer — PLAT-10775).
   */
  maxMenuHeight?: number;
};

/** Imperative handle: lets a caller close the menu programmatically (e.g. after
 * an in-header action runs — PLAT-10732 dub kebab). */
export type ItemDropdownHandle = { close: () => void };

const ItemDropdownSelect = forwardRef<ItemDropdownHandle, Props>(
  (props, ref) => {
    const { width = undefined } = props;
    const [selectedItem, setSelectedItem] = useState<string | undefined | null>(
      props.defaultValue,
    );
    const [showDropdown, toggleDropdown] = useState<boolean>(false);
    useImperativeHandle(
      ref,
      () => ({ close: () => toggleDropdown(false) }),
      [],
    );

    const infoTooltipId = useId();
    const hasInfo = props.values.some(v => v.info);

    useEffect(() => {
      setSelectedItem(props.defaultValue);
    }, [props.defaultValue]);

    useEffect(() => {
      props.onOpenChange?.(showDropdown);
    }, [showDropdown, props.onOpenChange]);

    // Portal the menu to <body>, anchored directly below the trigger and
    // overlaying everything (no panel-overflow clipping). The hook owns
    // dismissal (click-outside, scroll, resize, Escape).
    const triggerRef = useRef<HTMLDivElement>(null);
    const { menuRef, style: menuStyle } = useAnchoredDropdown(
      showDropdown,
      triggerRef,
      () => toggleDropdown(false),
      props.maxMenuHeight ?? 260,
    );

    const ArrowDownIcon = (
      <svg width="12" height="13" viewBox="0 0 12 13" fill="none">
        <path d="M2 4.33008L6 8.33008L10 4.33008" stroke="var(--accent)" />
      </svg>
    );

    const selectedValue = props.values.find(v => v.value === selectedItem);
    // The rows actually rendered; see `hideSelectedRow`.
    const rowValues = props.hideSelectedRow
      ? props.values.filter(v => v.value !== selectedItem)
      : props.values;

    // With a pinned header and/or footer, only the value rows should scroll —
    // the chrome stays fully visible (e.g. the "Dub with AI Voice" button must
    // never scroll out of view, PLAT-10775). Without chrome, keep the flat
    // structure so plain selects scroll as one column (no behaviour change).
    const hasChrome = props.menuHeader != null || props.menuFooter != null;
    const headerNode =
      typeof props.menuHeader === 'function'
        ? props.menuHeader(() => toggleDropdown(false))
        : props.menuHeader;
    const footerNode =
      typeof props.menuFooter === 'function'
        ? props.menuFooter(() => toggleDropdown(false))
        : props.menuFooter;

    // Edge-fade affordance: track whether the row list has more content above
    // or below the fold so we can fade that edge (and show a scrollbar) —
    // otherwise a clipped list looks complete and the extra rows go unnoticed
    // (PLAT-10775). Only meaningful in the pinned-chrome (inner-scroll) layout.
    const listRef = useRef<HTMLDivElement>(null);
    const [scrollEdges, setScrollEdges] = useState({
      top: false,
      bottom: false,
    });
    // When the list overflows, clip it mid-row so a sliver of the next row peeks
    // at the fold — an unmistakable "there's more below" cue that works on any
    // background (unlike the fade). Null = no peek (list fits, or not measurable).
    const [peekHeight, setPeekHeight] = useState<number | null>(null);
    const syncScrollEdges = () => {
      const el = listRef.current;
      if (!el) return;
      const top = el.scrollTop > 1;
      const bottom = el.scrollTop + el.clientHeight < el.scrollHeight - 1;
      setScrollEdges(prev =>
        prev.top === top && prev.bottom === bottom ? prev : { top, bottom },
      );

      // Peek: measure the row stride and cap the list to (N - 0.5) rows so the
      // last row is half-cut. Idempotent — re-measuring the capped height yields
      // the same value, so the ResizeObserver doesn't loop. Guarded on stride>0
      // (jsdom reports 0, so tests skip the peek).
      const rows = el.children;
      const overflowing = el.scrollHeight - el.clientHeight > 1;
      const stride =
        rows.length >= 2
          ? (rows[1] as HTMLElement).offsetTop -
            (rows[0] as HTMLElement).offsetTop
          : rows.length === 1
            ? (rows[0] as HTMLElement).offsetHeight + 2
            : 0;
      let nextPeek: number | null = null;
      if (overflowing && stride > 0) {
        const fit = Math.max(1, Math.round(el.clientHeight / stride));
        nextPeek = Math.round((fit - 0.5) * stride);
      }
      setPeekHeight(prev => (prev === nextPeek ? prev : nextPeek));
    };
    // Recompute whenever the menu opens, its rows change, OR its layout settles.
    // `menuStyle` is set asynchronously by useAnchoredDropdown (starts null), so
    // the list only mounts once it's non-null — depend on it so the first
    // measurement happens after the row list actually exists and is constrained
    // to its max-height. A `requestAnimationFrame` measures after layout/paint
    // (so `scrollHeight` vs `clientHeight` is accurate), and a ResizeObserver
    // keeps the bottom-fade correct across the different header heights (Dub
    // button vs. Voice toggle vs. splitter picker) — so the "there's more below"
    // cue is visible the moment the menu opens whenever the list overflows
    // (PLAT-10775).
    useEffect(() => {
      if (!showDropdown || !hasChrome || !menuStyle) {
        // Clear the peek so the next open re-measures from the natural height
        // instead of momentarily applying a stale clip.
        setPeekHeight(null);
        return undefined;
      }
      const el = listRef.current;
      if (!el) return undefined;
      const raf = requestAnimationFrame(syncScrollEdges);
      const observer = new ResizeObserver(syncScrollEdges);
      observer.observe(el);
      return () => {
        cancelAnimationFrame(raf);
        observer.disconnect();
      };
    }, [showDropdown, hasChrome, menuStyle, rowValues]);

    const renderDropdownItem = (item: {
      caption: React.ReactNode;
      value: string | null;
      isAction?: boolean;
      isHeading?: boolean;
      info?: string;
    }) =>
      item.isHeading ? (
        <GroupHeading key={item.value} role="presentation">
          {item.caption}
        </GroupHeading>
      ) : (
        <DropdownItem
          styleFont={props.styleFont}
          item={item.value}
          isSelected={item.value === selectedItem}
          key={item.value}
          onClick={() => {
            // Action rows run a side effect without becoming the selected
            // value, so the trigger keeps displaying the prior selection.
            if (!item.isAction) {
              setSelectedItem(item.value);
              if (props.getValue) {
                props.getValue(item.value!);
              }
            }

            props.action(props.propertyName, item.value);
            toggleDropdown(false);
          }}
        >
          {item.caption}
          {item.info && (
            <RowInfo
              data-tooltip-id={infoTooltipId}
              data-tooltip-content={item.info}
            >
              <InfoIconSmall />
            </RowInfo>
          )}
        </DropdownItem>
      );

    return (
      <Main width={width} className={props.className}>
        <Selected
          width={width}
          ref={triggerRef}
          onClick={() => {
            toggleDropdown(!showDropdown);
          }}
        >
          <SelectedContent
            styleFont={props.styleFont}
            selectedItem={selectedItem}
          >
            {selectedValue?.caption || ''}
          </SelectedContent>{' '}
          {<ToggleIcon>{ArrowDownIcon}</ToggleIcon>}
        </Selected>

        {showDropdown &&
          menuStyle &&
          createPortal(
            <Dropdown
              ref={menuRef}
              // With pinned chrome the outer panel must clip (not scroll) so
              // only the inner list scrolls — otherwise the header/footer would
              // still scroll with the whole column.
              style={
                hasChrome ? { ...menuStyle, overflowY: 'hidden' } : menuStyle
              }
              className={props.menuClassName}
            >
              {hasChrome ? (
                <>
                  {headerNode != null && <Chrome>{headerNode}</Chrome>}
                  <ScrollArea>
                    <ScrollableList
                      ref={listRef}
                      data-dropdown-list
                      onScroll={syncScrollEdges}
                      // Clip mid-row so the next row peeks at the fold when the
                      // list overflows (PLAT-10775).
                      style={
                        peekHeight != null
                          ? { maxHeight: peekHeight }
                          : undefined
                      }
                    >
                      {rowValues.map(item => renderDropdownItem(item))}
                    </ScrollableList>
                    <EdgeFade $side="top" $visible={scrollEdges.top} />
                    <EdgeFade $side="bottom" $visible={scrollEdges.bottom} />
                  </ScrollArea>
                  {footerNode != null && <Chrome>{footerNode}</Chrome>}
                </>
              ) : (
                <>
                  {headerNode}
                  {rowValues.map(item => renderDropdownItem(item))}
                  {footerNode}
                </>
              )}
              {hasInfo && (
                <Tooltip
                  id={infoTooltipId}
                  place="top"
                  positionStrategy="fixed"
                  noArrow
                  opacity={1}
                  // Above the menu, which already sits above modal backdrops.
                  style={{
                    ...PARAGRAPH_TOOLTIP_STYLE,
                    zIndex: TOP_LAYER_Z.tooltip,
                  }}
                />
              )}
            </Dropdown>,
            document.body,
          )}
      </Main>
    );
  },
);

export default ItemDropdownSelect;

const Main = styled.div<{
  width?: string;
}>`
  position: relative;
  font-family: var(--font-primary);
  font-size: var(--text-sm);
`;
const Selected = styled.div.attrs({
  className: 'Selected',
})<{
  width?: string;
}>`
  border: 1px solid var(--border);
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 24px;
  cursor: pointer;

  padding: 8px 16px;
  border-radius: 8px;
  font-family: var(--font-primary);
  font-size: var(--text-sm);

  /*
   * Bordered-field-primitive hover standard — see
   * docs/design-system.md "Form Fields (Bordered Primitives)".
   * No focus-visible state here because Selected is a <div>, not
   * natively focusable. Future a11y pass should add tabindex + role +
   * the accent border on focus.
   */
  transition: border-color var(--transition-fast);
  &:hover {
    border-color: var(--text-muted);
  }
`;

const SelectedContent = styled.span<{
  styleFont?: boolean;
  selectedItem?: string | null;
}>`
  font-size: var(--text-sm);
  ${props =>
    props.styleFont &&
    props.selectedItem &&
    css`
      font-family: ${props.selectedItem};
    `}

  ${props =>
    !props.styleFont &&
    css`
      font-family: var(--font-primary);
    `}
`;
// Positioning (position/top/left/width/max-height/overflow/z-index) is
// supplied at runtime by useAnchoredDropdown via an inline style — the menu
// portals to <body> and anchors below the trigger. Only visuals live here.
const Dropdown = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  border: 1px solid var(--border);
  border-radius: 8px;
  /* A consumer may lift its menu one depth step by setting --menu-surface on
     the panel (via menuClassName) — e.g. the transcript language menu sits on
     --surface-subtle so it doesn't blend into the page. The edge fades below
     read the same variable so they always fade into the panel's own surface.
     Default: --background, the dropdown standard (PLAT-11563). */
  background-color: var(--menu-surface, var(--background));
  box-shadow: var(--elevation-toast);
  padding: 4px;
  /* No cursor on the panel itself: the rows carry their own pointer, and a
     pinned header/footer holds labels and headings that are not clickable
     (PLAT-11563). */

  & {
    scrollbar-color: var(--gray-400) transparent;
  }
  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-thumb {
    background-color: transparent;
  }
`;

// Positioned wrapper around the scroll region so the edge fades can anchor to
// the list's top/bottom (not the whole menu). Carries the flex sizing so the
// list inside can shrink and scroll (PLAT-10775).
const ScrollArea = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
`;

// Scroll region holding just the value rows when a header/footer is present, so
// the chrome stays pinned and only the list scrolls (PLAT-10775). `min-height:
// 0` lets it shrink below its content; a visible scrollbar thumb signals that
// the list overflows (the edge fades reinforce it).
const ScrollableList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;

  & {
    scrollbar-color: var(--gray-400) transparent;
  }
  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-thumb {
    background-color: var(--gray-400);
    border-radius: 4px;
  }
`;

// Soft fade at the top/bottom edge of the list, shown only when there's more
// content in that direction — the cue that the list scrolls to more rows. Inset
// from the right so it never sits over the scrollbar; non-interactive.
const EdgeFade = styled.div<{ $side: 'top' | 'bottom'; $visible: boolean }>`
  position: absolute;
  left: 0;
  right: 8px;
  pointer-events: none;
  opacity: ${props => (props.$visible ? 1 : 0)};
  transition: opacity 120ms ease;
  ${props =>
    props.$side === 'top'
      ? css`
          top: 0;
          height: 18px;
          background: linear-gradient(
            to bottom,
            var(--menu-surface, var(--background)),
            transparent
          );
        `
      : css`
          bottom: 0;
          /* Taller + a solid-hold near the edge so it visibly dims the peeking
             partial row on the dark theme (a short black→transparent gradient
             reads as nothing here). PLAT-10775. */
          height: 36px;
          background: linear-gradient(
            to top,
            var(--menu-surface, var(--background)) 0%,
            var(--menu-surface, var(--background)) 25%,
            transparent 100%
          );
        `}
`;

// Wrapper that pins a header/footer node so it never shrinks or scrolls with
// the list.
const Chrome = styled.div`
  flex-shrink: 0;
`;

// A group label between option rows: quiet, small, not a target.
const GroupHeading = styled.span`
  display: flex;
  align-items: center;
  min-height: 24px;
  padding: 6px 10px 0;
  color: var(--text-muted);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  cursor: default;
`;

const DropdownItem = styled.span<{
  styleFont?: boolean;
  item?: string | null;
  isSelected?: boolean;
}>`
  display: flex;
  align-items: center;
  min-height: 32px;
  padding: 0 10px;
  border-radius: 6px;
  font-size: var(--text-sm);
  line-height: 1.2;
  cursor: pointer;
  color: ${props =>
    props.isSelected ? 'var(--accent)' : 'var(--text-secondary)'};
  font-family: ${props =>
    props.styleFont && props.item ? props.item : 'var(--font-primary)'};
  transition:
    background-color 120ms ease,
    color 120ms ease;

  ${props =>
    props.item === 'transparent' &&
    css`
      background: linear-gradient(
        to left top,
        rgba(255, 255, 255, 1) 0%,
        rgba(255, 255, 255, 1) calc(50% - 0.8px),
        rgb(255, 0, 0) 50%,
        rgba(255, 255, 255, 1) calc(50% + 0.8px),
        rgba(255, 255, 255, 1) 100%
      );
    `}

  &:hover {
    background-color: var(--surface-elevated);
    color: ${props =>
      props.isSelected ? 'var(--accent)' : 'var(--foreground)'};
  }
`;

const RowInfo = styled.span`
  display: inline-flex;
  align-items: center;
  margin-left: var(--spacing-xs);
  color: var(--text-muted);
  cursor: help;
`;

const ToggleIcon = styled.button`
  border: 0;
  outline: 0;
  background-color: transparent;
`;
